import type { HTMLAttributes } from 'react';
import { X } from 'lucide-react';
import { Button } from '../Button';

export interface ToastActionProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  'children'
> {
  /** Leading text — hugs, then truncates on one line (the task name). */
  name: string;
  /** Trailing text — never truncates ("completed"). */
  suffix: string;
  /** Action button label ("Undo"). */
  actionLabel: string;
  onAction: () => void;
  /** Renders the ✕ when provided. */
  onClose?: () => void;
  /** Accessible name of the ✕. */
  closeLabel?: string;
}

/**
 * `Toast / Action` (Figma 10964:5429) — a bottom toast the parent can act on
 * what they just did. First use: Undo after completing a task (YAR-476).
 *
 * Figma draws two variants, Overflow=none and Overflow=truncate, only because
 * Figma cannot hug-then-shrink. Here it is one row: the name is a flex item
 * that hugs its text and, once the row runs out of room, shrinks and
 * ellipsizes; the suffix is `shrink-0`, so "completed" never truncates.
 *
 * Presentational only. Placement, width, motion, the visible-time clock and
 * the live-region announcement belong to `BottomToastSlot`, which is the one
 * place this is mounted in the app.
 *
 * Tap areas: Undo is 28px tall and ✕ 24px as drawn. Each gets an invisible
 * `before:` box that extends it to 44px without changing how it looks.
 */
export function ToastAction({
  name,
  suffix,
  actionLabel,
  onAction,
  onClose,
  closeLabel = 'Close',
  className = '',
  ...rest
}: ToastActionProps) {
  return (
    <div
      className={[
        // Figma: surface/default fill, 1px border/neutral/default-subtle,
        // 8px radius, 12px padding + gap, 52px tall, "md - popover" shadow.
        'flex h-[52px] w-full items-center gap-3 rounded-md border border-border bg-surface p-3',
        'shadow-[0px_0px_8px_-2px_rgba(56,64,74,0.08)]',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      {/* Message — body-md/medium in text/neutral/default, 6px gap. */}
      <p className="flex min-w-0 flex-1 items-center gap-1.5 whitespace-nowrap text-base font-medium text-muted-foreground">
        <span className="min-w-0 truncate">{name}</span>
        <span className="shrink-0">{suffix}</span>
      </p>

      <Button
        type="button"
        variant="tertiary"
        size="sm"
        onClick={onAction}
        className="relative shrink-0 before:absolute before:inset-x-0 before:-inset-y-2"
      >
        {actionLabel}
      </Button>

      {onClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label={closeLabel}
          className="relative inline-flex size-6 shrink-0 cursor-pointer items-center justify-center text-muted-foreground before:absolute before:-inset-2.5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        >
          <X
            size={24}
            aria-hidden="true"
          />
        </button>
      )}
    </div>
  );
}
